import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MAX_TILE_TEXT,
  type JeopardyClueBody,
  type JeopardyClueChoice,
  type JeopardyLockout,
  type JeopardyQuestionType,
  type JeopardySpeedBonus,
} from '@gsf/games';
import { bank, DEFAULT_OPEN_ANSWER_SECONDS, type ClueSearchResult, type PoolQuestion } from './content';

/**
 * The clue editor panel below the grid. The form keeps every field across type
 * switches, so choices survive a detour; only fields the chosen type uses are saved.
 */

interface Props {
  clueId: string;
  categoryName: string;
  /** True while this clue is the one on screen — a type change is refused mid-clue. */
  inPlay: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  /** Called after a successful write, so the host can refresh its draft counts. */
  onSaved: () => void;
  notify: (message: string) => void;
}

const ANSWER_TYPES: readonly JeopardyQuestionType[] = ['verbal', 'textInput', 'numericInput'];
const CHOICE_TYPES: readonly JeopardyQuestionType[] = ['multipleChoice', 'multipleAnswer'];
const isChoice = (t: JeopardyQuestionType): boolean => CHOICE_TYPES.includes(t);
const isCollect = (t: JeopardyQuestionType): boolean => t !== 'verbal';

const QUESTION_TYPE_LABELS: Record<JeopardyQuestionType, string> = {
  verbal: 'Buzz in',
  textInput: 'Typed text',
  numericInput: 'Typed number',
  multipleChoice: 'Multiple choice',
  multipleAnswer: 'Multiple answer',
};

// Lead each hint with who answers — that distinction is the thing hosts most need up front.
const QUESTION_TYPE_HINTS: Record<JeopardyQuestionType, string> = {
  verbal: 'Only the quickest answers — players buzz in; the host judges by ear.',
  textInput: 'Everyone answers — players type a text answer.',
  numericInput: 'Everyone answers — players type a number; auto-graded on an exact match.',
  multipleChoice: 'Everyone answers — players pick the one correct answer.',
  multipleAnswer: 'Everyone answers — players pick one or more correct answers.',
};

const MAX_CHOICES = 8;
const DEFAULT_CHOICE_SLOTS = 4;

/**
 * Keep only what reads as a number: digits, one leading minus, one decimal point. Used on
 * the numeric clue's answer field so a non-number can't be typed or saved.
 */
export function onlyNumeric(raw: string): string {
  let s = raw.replace(/[^\d.-]/gu, '');
  s = s.replace(/(?!^)-/gu, ''); // a minus only at the front
  const dot = s.indexOf('.');
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./gu, ''); // one dot
  return s;
}

let choiceSeq = 0;
const freshChoice = (): JeopardyClueChoice => ({ id: `ch_${(choiceSeq += 1)}_${Date.now()}`, text: '' });
const blankChoices = (): JeopardyClueChoice[] =>
  Array.from({ length: DEFAULT_CHOICE_SLOTS }, freshChoice);

export function ClueEditorPanel({
  clueId,
  categoryName,
  inPlay,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
  onSaved,
  notify,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'content' | 'scoring'>('content');

  // The clue's stored shape, kept so fields the current type does not use round-trip
  // untouched (a category id, a stored lockout on a collect clue, and so on).
  const [base, setBase] = useState<JeopardyClueBody | null>(null);

  const [prompt, setPrompt] = useState('');
  const [questionType, setQuestionType] = useState<JeopardyQuestionType>('verbal');
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [choices, setChoices] = useState<JeopardyClueChoice[]>(blankChoices);
  const [correctChoiceIds, setCorrectChoiceIds] = useState<string[]>([]);
  const [scoringKind, setScoringKind] = useState<'static' | 'accrued'>('static');
  const [points, setPoints] = useState('100');
  const [maxWager, setMaxWager] = useState('100');
  const [negativeEnabled, setNegativeEnabled] = useState(false);
  const [negativePoints, setNegativePoints] = useState('100');
  const [partialScoring, setPartialScoring] = useState(false);
  const [tileCustom, setTileCustom] = useState(false);
  const [tileText, setTileText] = useState('');
  const [lockout, setLockout] = useState<JeopardyLockout>('host_enable');
  const [timed, setTimed] = useState(false);
  const [answerSeconds, setAnswerSeconds] = useState(String(DEFAULT_OPEN_ANSWER_SECONDS));
  // Speed bonus (issue #11), collect clues only. Each mode's fields keep their own state so
  // switching between them does not lose what was typed.
  const [speedKind, setSpeedKind] = useState<'none' | 'static' | 'decay'>('none');
  const [speedPoints, setSpeedPoints] = useState('100');
  const [speedScope, setSpeedScope] = useState<'fastest' | 'before'>('fastest');
  const [speedBeforeSeconds, setSpeedBeforeSeconds] = useState('10');
  const [speedMax, setSpeedMax] = useState('100');
  const [speedMin, setSpeedMin] = useState('0');
  const [speedSeconds, setSpeedSeconds] = useState(String(DEFAULT_OPEN_ANSWER_SECONDS));

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<'clues' | 'bank'>('clues');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ClueSearchResult[]>([]);
  const [bankResults, setBankResults] = useState<PoolQuestion[]>([]);

  // Load the clue whenever the panel points at a new tile.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearchOpen(false);
    bank
      .getClueBody(clueId)
      .then((body) => {
        if (cancelled) return;
        setBase(body);
        setPrompt(body.prompt);
        setQuestionType(body.questionType);
        setCorrectAnswer('correctAnswer' in body ? body.correctAnswer : '');
        if (body.questionType === 'multipleChoice' || body.questionType === 'multipleAnswer') {
          setChoices(body.choices.length > 0 ? body.choices.map((c) => ({ ...c })) : blankChoices());
          setCorrectChoiceIds([...body.correctChoiceIds]);
          setPartialScoring(body.questionType === 'multipleAnswer' && body.partialScoring === true);
        } else {
          setChoices(blankChoices());
          setCorrectChoiceIds([]);
          setPartialScoring(false);
        }
        setScoringKind(body.scoring.kind === 'accrued' ? 'accrued' : 'static');
        setPoints(String(body.scoring.kind === 'static' ? body.scoring.points : 100));
        setMaxWager(String(body.scoring.kind === 'accrued' ? body.scoring.maxWager : 100));
        setNegativeEnabled(body.negativePoints !== undefined);
        setNegativePoints(String(body.negativePoints ?? 100));
        setTileCustom(body.tileDisplay.kind === 'custom');
        setTileText(body.tileDisplay.kind === 'custom' ? body.tileDisplay.text : '');
        setLockout(body.lockout);
        setTimed((body.answerSeconds ?? 0) > 0);
        setAnswerSeconds(
          String(body.answerSeconds && body.answerSeconds > 0 ? body.answerSeconds : DEFAULT_OPEN_ANSWER_SECONDS),
        );
        const sb = body.speedBonus;
        setSpeedKind(sb?.kind ?? 'none');
        if (sb?.kind === 'static') {
          setSpeedPoints(String(sb.points));
          setSpeedScope(sb.scope);
          if (sb.beforeSeconds !== undefined) setSpeedBeforeSeconds(String(sb.beforeSeconds));
        } else if (sb?.kind === 'decay') {
          setSpeedMax(String(sb.maxPoints));
          setSpeedMin(String(sb.minPoints));
          setSpeedSeconds(String(sb.seconds));
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load that clue.');
        setLoading(false);
      });
    setTab('content');
    return () => {
      cancelled = true;
    };
  }, [clueId]);

  // Escape closes the popup, matching the round editor.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Switching to a choice type with no slots yet gets the default four.
  function changeType(next: JeopardyQuestionType) {
    if (isChoice(next) && choices.every((c) => c.text.trim() === '') && choices.length === 0) {
      setChoices(blankChoices());
    }
    if (isChoice(next) && choices.length === 0) setChoices(blankChoices());
    setQuestionType(next);
  }

  function setChoiceText(id: string, text: string) {
    setChoices((cs) => cs.map((c) => (c.id === id ? { ...c, text } : c)));
  }
  function addChoice() {
    setChoices((cs) => (cs.length >= MAX_CHOICES ? cs : [...cs, freshChoice()]));
  }
  function removeChoice(id: string) {
    setChoices((cs) => cs.filter((c) => c.id !== id));
    setCorrectChoiceIds((ids) => ids.filter((c) => c !== id));
  }
  function toggleCorrect(id: string) {
    if (questionType === 'multipleChoice') {
      setCorrectChoiceIds([id]);
    } else {
      setCorrectChoiceIds((ids) => (ids.includes(id) ? ids.filter((c) => c !== id) : [...ids, id]));
    }
  }

  const runSearch = useCallback((mode: 'clues' | 'bank', q: string) => {
    setSearchQuery(q);
    if (mode === 'bank') {
      bank
        .searchQuestions(q)
        .then(setBankResults)
        .catch(() => setBankResults([]));
    } else {
      bank
        .searchClues(q)
        .then(setSearchResults)
        .catch(() => setSearchResults([]));
    }
  }, []);

  function switchSearchMode(mode: 'clues' | 'bank') {
    setSearchMode(mode);
    runSearch(mode, searchQuery);
  }

  function pickResult(r: ClueSearchResult) {
    // Fills prompt and correct answer only; never the type, scoring or choices.
    setPrompt(r.prompt);
    setCorrectAnswer(r.answer);
    setSearchOpen(false);
  }

  /**
   * Insert a pool question into this clue. Prompt and typed answer always; distractors
   * become multiple-choice choices, but only when the type is free to change (not mid-play).
   */
  function pickQuestion(q: PoolQuestion) {
    setPrompt(q.prompt);
    const distractors = q.incorrectAnswers.filter((a) => a.trim() !== '');
    if (distractors.length > 0 && !inPlay) {
      const correct = { ...freshChoice(), text: q.correctAnswer };
      const wrong = distractors.map((text) => ({ ...freshChoice(), text }));
      setChoices([correct, ...wrong]);
      setCorrectChoiceIds([correct.id]);
      setQuestionType('multipleChoice');
    } else {
      setCorrectAnswer(q.correctAnswer);
    }
    setSearchOpen(false);
  }

  /** Assemble the form into a v2 body, or a message naming what is wrong. */
  function assemble(): JeopardyClueBody | string {
    if (base === null) return 'The clue is still loading.';
    // A speed bonus only makes sense where everyone answers at once (a collect clue with
    // fixed scoring), so it is dropped on any other type.
    let speedBonus: JeopardySpeedBonus | undefined;
    if (isCollect(questionType) && scoringKind !== 'accrued' && speedKind !== 'none') {
      if (speedKind === 'static') {
        speedBonus = {
          kind: 'static',
          points: Math.max(1, Number(speedPoints) || 0),
          scope: speedScope,
          ...(speedScope === 'before'
            ? { beforeSeconds: Math.max(1, Number(speedBeforeSeconds) || 0) }
            : {}),
        };
      } else {
        const maxPoints = Math.max(0, Number(speedMax) || 0);
        const minPoints = Math.max(0, Number(speedMin) || 0);
        if (minPoints > maxPoints) return 'Speed bonus: the minimum cannot exceed the maximum.';
        speedBonus = { kind: 'decay', maxPoints, minPoints, seconds: Math.max(1, Number(speedSeconds) || 0) };
      }
    }
    const common = {
      categoryId: base.categoryId,
      prompt: prompt.trim(),
      position: base.position,
      scoring:
        scoringKind === 'accrued'
          ? { kind: 'accrued' as const, maxWager: Math.max(1, Number(maxWager) || 0) }
          : { kind: 'static' as const, points: Math.max(1, Number(points) || 0) },
      // Negative points is a modifier on static/accrued only.
      ...(negativeEnabled ? { negativePoints: Math.max(0, Number(negativePoints) || 0) } : {}),
      tileDisplay: tileCustom
        ? { kind: 'custom' as const, text: tileText }
        : { kind: 'points' as const },
      lockout,
      answerSeconds: isCollect(questionType) && timed ? Math.max(1, Number(answerSeconds) || 0) : null,
      ...(speedBonus ? { speedBonus } : {}),
    };

    if (questionType === 'multipleChoice' || questionType === 'multipleAnswer') {
      const filled = choices.filter((c) => c.text.trim() !== '').map((c) => c.id);
      const marked = correctChoiceIds.filter((id) => filled.includes(id));
      if (questionType === 'multipleChoice') {
        if (marked.length !== 1) return 'Mark exactly one correct answer.';
        return { ...common, questionType: 'multipleChoice', choices, correctChoiceIds: marked };
      }
      if (marked.length < 1) return 'Mark at least one correct answer.';
      return {
        ...common,
        questionType: 'multipleAnswer',
        choices,
        correctChoiceIds: marked,
        ...(partialScoring ? { partialScoring: true } : {}),
      };
    }
    // The three answer types share one shape and differ only by the discriminant, which
    // is a union here — so the cast is what stands in for a per-literal branch.
    const qt: 'verbal' | 'textInput' | 'numericInput' = questionType;
    return { ...common, questionType: qt, correctAnswer: correctAnswer.trim() } as JeopardyClueBody;
  }

  async function save(thenNext: boolean) {
    const body = assemble();
    if (typeof body === 'string') {
      // Every assemble error is a Content-tab field (prompt, answer, choices), so surface it there.
      setTab('content');
      setError(body);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await bank.saveClueBody(clueId, body);
      onSaved();
      if (thenNext && hasNext) onNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that clue.');
      notify('That clue could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const showLockout = questionType === 'verbal' && scoringKind !== 'accrued';
  const showTimer = isCollect(questionType);
  // Speed bonus pays a quick correct answer, so it needs everyone answering at once.
  const showSpeedBonus = isCollect(questionType) && scoringKind !== 'accrued';
  const answerLabel =
    (questionType === 'numericInput' ? 'Correct Number' : 'Correct Answer') + ' (Shown By Host Control)';

  const header = useMemo(
    () => (inPlay ? `Editing a clue that is on screen — ${categoryName}` : `Editing — ${categoryName}`),
    [inPlay, categoryName],
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-wide clue-editor-panel"
        role="dialog"
        aria-label="Clue editor"
        onClick={(event) => event.stopPropagation()}
      >
      <div className="sheet-head">
        <h2 className="panel-title" style={{ margin: 0 }}>
          {header}
        </h2>
        <span className="spacer" />
        <button className="btn btn-sm btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="col">
          <div className="row clue-editor-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'content'}
              className={`btn btn-sm${tab === 'content' ? ' btn-primary' : ' btn-ghost'}`}
              onClick={() => setTab('content')}
            >
              Content &amp; Bank
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'scoring'}
              className={`btn btn-sm${tab === 'scoring' ? ' btn-primary' : ' btn-ghost'}`}
              onClick={() => setTab('scoring')}
            >
              Scoring
            </button>
          </div>

          <div className="clue-editor-tabbody">
          <div className={`col clue-editor-tabpanel${tab === 'content' ? ' is-active' : ''}`} role="tabpanel">
          {/* The tile face comes first — it is what players see on the board before the clue
              is picked. The section headers keep it from reading as a second clue field. */}
          <h3 className="editor-section">Tile face — what players see on the board</h3>
          <label className="field-check">
            <input type="checkbox" checked={tileCustom} onChange={(e) => setTileCustom(e.target.checked)} />
            <span>Show custom text instead of the point value</span>
          </label>
          {tileCustom ? (
            <div className="field">
              <label htmlFor="clue-tile">Tile Text (Before Clue is Revealed)</label>
              <input id="clue-tile" value={tileText} maxLength={MAX_TILE_TEXT} onChange={(e) => setTileText(e.target.value)} />
            </div>
          ) : null}

          <h3 className="editor-section">Clue</h3>
          <div className="field">
            <label>Question type</label>
            <div className="seg-group" role="group" aria-label="Question type">
              {[...ANSWER_TYPES, ...CHOICE_TYPES].map((t) => (
                <button
                  type="button"
                  key={t}
                  className={`btn btn-sm${questionType === t ? ' btn-primary' : ' btn-ghost'}`}
                  aria-pressed={questionType === t}
                  title={QUESTION_TYPE_HINTS[t]}
                  disabled={inPlay}
                  onClick={() => changeType(t)}
                >
                  {QUESTION_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
            <p className="faint" style={{ margin: 0 }}>
              {inPlay ? 'Return to the board to change the clue type.' : QUESTION_TYPE_HINTS[questionType]}
            </p>
          </div>

          <div className="field">
            <label htmlFor="clue-prompt">Prompt (Shown After Reveal)</label>
            <textarea
              id="clue-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
            />
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => {
                setSearchOpen((v) => !v);
                if (!searchOpen) runSearch(searchMode, '');
              }}
            >
              {searchOpen ? 'Hide search' : 'Search existing clues'}
            </button>
          </div>

          {searchOpen ? (
            <div className="clue-search">
              <div className="seg-group" role="group" aria-label="Search source" style={{ marginBottom: '.4rem' }}>
                <button
                  type="button"
                  className={`btn btn-sm${searchMode === 'clues' ? ' btn-primary' : ' btn-ghost'}`}
                  aria-pressed={searchMode === 'clues'}
                  onClick={() => switchSearchMode('clues')}
                >
                  This board
                </button>
                <button
                  type="button"
                  className={`btn btn-sm${searchMode === 'bank' ? ' btn-primary' : ' btn-ghost'}`}
                  aria-pressed={searchMode === 'bank'}
                  onClick={() => switchSearchMode('bank')}
                >
                  Question bank
                </button>
              </div>
              <input
                autoFocus
                placeholder={
                  searchMode === 'bank'
                    ? 'Search prompt, answer or tag…'
                    : 'Search prompt, answer or category…'
                }
                value={searchQuery}
                onChange={(e) => runSearch(searchMode, e.target.value)}
              />
              <ul className="clue-search-results">
                {searchMode === 'bank' ? (
                  bankResults.length === 0 ? (
                    <li className="faint">No matches in your question bank.</li>
                  ) : (
                    bankResults.map((q) => (
                      <li key={q.id}>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => pickQuestion(q)}>
                          <strong>{q.prompt || '(no prompt)'}</strong>
                          <span className="faint"> — {q.correctAnswer}</span>
                          {q.incorrectAnswers.length > 0 ? (
                            <span className="faint"> · {q.incorrectAnswers.length} choice{q.incorrectAnswers.length === 1 ? '' : 's'}</span>
                          ) : null}
                        </button>
                      </li>
                    ))
                  )
                ) : searchResults.length === 0 ? (
                  <li className="faint">No matches.</li>
                ) : (
                  searchResults.map((r) => (
                    <li key={r.id}>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => pickResult(r)}>
                        <strong>{r.prompt || '(no prompt)'}</strong>
                        <span className="faint"> — {r.answer}</span>
                        {r.category ? <span className="faint"> · {r.category}</span> : null}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          ) : null}

          {isChoice(questionType) ? (
            <div className="field">
              <label>Choices</label>
              <p className="faint" style={{ margin: '0 0 .4rem' }}>
                {questionType === 'multipleChoice'
                  ? 'Mark the one correct answer. Blank rows are not shown to players.'
                  : 'Mark every correct answer. Blank rows are not shown to players.'}
              </p>
              {choices.map((c) => (
                <div className="row choice-row" key={c.id} style={{ alignItems: 'center' }}>
                  <input
                    type={questionType === 'multipleChoice' ? 'radio' : 'checkbox'}
                    name="clue-correct"
                    checked={correctChoiceIds.includes(c.id)}
                    onChange={() => toggleCorrect(c.id)}
                    aria-label="Correct answer"
                  />
                  <input
                    style={{ flex: 1 }}
                    value={c.text}
                    placeholder="Choice text"
                    onChange={(e) => setChoiceText(c.id, e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => removeChoice(c.id)}
                    aria-label="Remove choice"
                  >
                    ×
                  </button>
                </div>
              ))}
              {choices.length < MAX_CHOICES ? (
                <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={addChoice}>
                  Add another choice
                </button>
              ) : null}
              {questionType === 'multipleAnswer' ? (
                <label className="field-check" style={{ marginTop: '.4rem' }}>
                  <input type="checkbox" checked={partialScoring} onChange={(e) => setPartialScoring(e.target.checked)} />
                  <span>Partial credit for a partly-right answer</span>
                </label>
              ) : null}
            </div>
          ) : (
            <div className="field">
              <label htmlFor="clue-answer">{answerLabel}</label>
              <input
                id="clue-answer"
                value={correctAnswer}
                inputMode={questionType === 'numericInput' ? 'decimal' : 'text'}
                onChange={(e) =>
                  setCorrectAnswer(
                    questionType === 'numericInput' ? onlyNumeric(e.target.value) : e.target.value,
                  )
                }
              />
              {questionType === 'numericInput' ? (
                <p className="faint" style={{ margin: 0 }}>
                  Numbers only — players enter a number, auto-graded on an exact match.
                </p>
              ) : null}
            </div>
          )}

          {showLockout ? (
            <div className="field">
              <label htmlFor="clue-lockout">Buzzer lockout</label>
              <select id="clue-lockout" value={lockout} onChange={(e) => setLockout(e.target.value as JeopardyLockout)}>
                <option value="host_enable">Host unlocks</option>
                <option value="auto_unlock">Unlock on reveal</option>
              </select>
            </div>
          ) : null}

          {showTimer ? (
            <>
              <label className="field-check">
                <input type="checkbox" checked={timed} onChange={(e) => setTimed(e.target.checked)} />
                <span>Count down with a timer</span>
              </label>
              {timed ? (
                <div className="field">
                  <label htmlFor="clue-seconds">Timer (seconds)</label>
                  <input id="clue-seconds" type="number" value={answerSeconds} min={5} max={300} onChange={(e) => setAnswerSeconds(e.target.value)} />
                </div>
              ) : (
                <p className="faint" style={{ margin: 0 }}>No timer — answers stay open until you close them.</p>
              )}
            </>
          ) : null}
          </div>

          <div className={`col clue-editor-tabpanel${tab === 'scoring' ? ' is-active' : ''}`} role="tabpanel">
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: '2 1 12rem' }}>
              <label htmlFor="clue-scoring">Scoring</label>
              <select
                id="clue-scoring"
                value={scoringKind}
                onChange={(e) => setScoringKind(e.target.value as 'static' | 'accrued')}
              >
                <option value="static">Fixed point value</option>
                <option value="accrued">Wager from own score</option>
              </select>
            </div>
            {scoringKind === 'static' ? (
              <div className="field" style={{ flex: '1 1 6rem' }}>
                <label htmlFor="clue-points">Points</label>
                <input id="clue-points" type="number" value={points} min={1} step={100} onChange={(e) => setPoints(e.target.value)} />
              </div>
            ) : (
              <div className="field" style={{ flex: '1 1 6rem' }}>
                <label htmlFor="clue-maxwager">Max wager</label>
                <input id="clue-maxwager" type="number" value={maxWager} min={1} step={100} onChange={(e) => setMaxWager(e.target.value)} />
              </div>
            )}
          </div>

          {/* Negative points (not on wager scoring) */}
          <label className="field-check">
            <input type="checkbox" checked={negativeEnabled} onChange={(e) => setNegativeEnabled(e.target.checked)} />
            <span>Deduct points on a wrong answer</span>
          </label>
          {negativeEnabled ? (
            <div className="field">
              <label htmlFor="clue-negative">Points deducted</label>
              <input id="clue-negative" type="number" value={negativePoints} min={0} step={100} onChange={(e) => setNegativePoints(e.target.value)} />
            </div>
          ) : null}

          {showSpeedBonus ? (
            <>
              <h3 className="editor-section">Speed bonus — reward a quick correct answer</h3>
              <div className="field">
                <label htmlFor="clue-speed">Bonus</label>
                <select
                  id="clue-speed"
                  value={speedKind}
                  onChange={(e) => setSpeedKind(e.target.value as 'none' | 'static' | 'decay')}
                >
                  <option value="none">No speed bonus</option>
                  <option value="static">Fixed bonus</option>
                  <option value="decay">Decaying bonus (quicker = more)</option>
                </select>
              </div>

              {speedKind === 'static' ? (
                <div className="row" style={{ alignItems: 'flex-end' }}>
                  <div className="field" style={{ flex: '1 1 6rem' }}>
                    <label htmlFor="clue-speed-points">Bonus points</label>
                    <input id="clue-speed-points" type="number" value={speedPoints} min={1} step={100} onChange={(e) => setSpeedPoints(e.target.value)} />
                  </div>
                  <div className="field" style={{ flex: '2 1 12rem' }}>
                    <label htmlFor="clue-speed-scope">Who gets it</label>
                    <select
                      id="clue-speed-scope"
                      value={speedScope}
                      onChange={(e) => setSpeedScope(e.target.value as 'fastest' | 'before')}
                    >
                      <option value="fastest">Single fastest correct answer</option>
                      <option value="before">Everyone correct before a time</option>
                    </select>
                  </div>
                  {speedScope === 'before' ? (
                    <div className="field" style={{ flex: '1 1 6rem' }}>
                      <label htmlFor="clue-speed-before">Within (seconds)</label>
                      <input id="clue-speed-before" type="number" value={speedBeforeSeconds} min={1} max={300} onChange={(e) => setSpeedBeforeSeconds(e.target.value)} />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {speedKind === 'decay' ? (
                <>
                  <div className="row" style={{ alignItems: 'flex-end' }}>
                    <div className="field" style={{ flex: '1 1 6rem' }}>
                      <label htmlFor="clue-speed-max">Max bonus</label>
                      <input id="clue-speed-max" type="number" value={speedMax} min={0} step={100} onChange={(e) => setSpeedMax(e.target.value)} />
                    </div>
                    <div className="field" style={{ flex: '1 1 6rem' }}>
                      <label htmlFor="clue-speed-min">Min bonus</label>
                      <input id="clue-speed-min" type="number" value={speedMin} min={0} step={100} onChange={(e) => setSpeedMin(e.target.value)} />
                    </div>
                    <div className="field" style={{ flex: '1 1 6rem' }}>
                      <label htmlFor="clue-speed-window">Window (seconds)</label>
                      <input id="clue-speed-window" type="number" value={speedSeconds} min={1} max={300} onChange={(e) => setSpeedSeconds(e.target.value)} />
                    </div>
                  </div>
                  <p className="faint" style={{ margin: 0 }}>
                    Bonus falls from the max at reveal to the min as the window runs; after it, none.
                    Players can still answer.
                  </p>
                </>
              ) : null}
            </>
          ) : null}
          </div>
          </div>

          <div className="row" style={{ marginTop: '.5rem' }}>
            <button type="button" className="btn btn-ghost" disabled={!hasPrev} onClick={onPrev}>
              ‹ Prev
            </button>
            <span className="spacer" />
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save(false)}>
              {saving ? 'Saving…' : 'Save clue'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={saving || !hasNext} onClick={() => void save(true)}>
              Save &amp; next ›
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
